import { useState } from "react";
import { Sparkles, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AIGenerateButtonProps {
  title: string;
  venue: string;
  date: string;
  startTime: string;
  endTime: string;
  price: number;
  creditCost?: number;
  tags: string[];
  existingDescription?: string;
  onGenerate: (description: string) => void;
  disabled?: boolean;
}

const AIGenerateButton = ({
  title,
  venue,
  date,
  startTime,
  endTime,
  price,
  creditCost,
  tags,
  existingDescription,
  onGenerate,
  disabled,
}: AIGenerateButtonProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const { toast } = useToast();

  const canGenerate = title.trim() && venue.trim();

  const handleGenerate = async () => {
    if (!canGenerate) {
      toast({
        title: "Missing information",
        description: "Please fill in the event title and venue first.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-event-description', {
        body: {
          title,
          venue,
          date,
          startTime,
          endTime,
          price,
          creditCost,
          tags,
          existingDescription: existingDescription?.trim() || undefined,
        },
      });

      if (error) {
        throw error;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      onGenerate(data.description);
      setHasGenerated(true);
      
      toast({
        title: "Description generated",
        description: "AI has created a description for your event.",
      });
    } catch (error) {
      console.error('Failed to generate description:', error);
      toast({
        title: "Generation failed",
        description: error instanceof Error ? error.message : "Failed to generate description. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleGenerate}
            disabled={disabled || isGenerating || !canGenerate}
            className="gap-1.5"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : hasGenerated ? (
              <>
                <RefreshCw className="w-4 h-4" />
                Regenerate
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Generate with AI
              </>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {!canGenerate 
            ? "Enter title and venue first" 
            : hasGenerated 
              ? "Generate a new description"
              : "Use AI to generate event description"
          }
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default AIGenerateButton;
